import type { SseEvent } from '@/shared/lib/sse/parser';

/* Pure decision for the global "Чат" nav signal, extracted from useChatSignal
   so the branchy semantics are unit-testable without a DOM or a live stream.

   A dispatcher should be signalled on exactly these kinds of incoming activity:
     1. a real operator message (author_type === 'OPERATOR'),
     2. a real consumer (B2C client) message (author_type === 'CONSUMER'),
     3. a brand-new geo-approval request the dispatcher must act on
        (kind === 'APPROVAL_REQUEST' && payload.status === 'PENDING'),
     4. a brand-new no-show card — the client's time ran out, the report is in, and the
        dispatcher has a few minutes to decide before the order closes by itself
        (kind === 'NO_SHOW_DECISION' && payload.status === 'PENDING'), and
     5. the notice that a no-show order was closed by its deadline because nobody decided
        (payload.event === 'NO_SHOW_AUTO_CANCELLED').

   4 и 5 добавлены 12.10.2026. Карточка неявки — системное сообщение, и до этого не зажигала ни
   значок, ни пуш: решение замечали, только если сидели в чате, а об автоотмене не узнавали вовсе
   (11.10.2026, выезд E313YK797). Ровно те же правила у пуша на сервере
   (`integrations::webpush::push_for_admin`).

   The dispatcher's own approve/reject republishes a SYSTEM message with a
   resolved status (APPROVED/REJECTED) — that must NOT signal, or every decision
   would re-light the badge. Malformed / non-chat events never signal. */

export interface ChatSignal {
  /** Whether this event should bump the badge / fire a cue. */
  signal: boolean;
  /** True when it asks for a decision (approval or no-show card) vs a plain message. */
  isApproval: boolean;
}

const NO_SIGNAL: ChatSignal = { signal: false, isApproval: false };

export function shouldSignalChat(ev: SseEvent): ChatSignal {
  if (ev.event !== 'chat_message') return NO_SIGNAL;

  let authorType: string | undefined;
  let kind: string | undefined;
  let status: string | undefined;
  let event: string | undefined;
  try {
    const parsed = JSON.parse(ev.data) as {
      author_type?: unknown;
      kind?: unknown;
      payload?: unknown;
    };
    if (typeof parsed.author_type === 'string') authorType = parsed.author_type;
    if (typeof parsed.kind === 'string') kind = parsed.kind;
    const payload = parsed.payload;
    if (payload && typeof payload === 'object') {
      if ('status' in payload) {
        const s = (payload as { status?: unknown }).status;
        if (typeof s === 'string') status = s;
      }
      if ('event' in payload) {
        const e = (payload as { event?: unknown }).event;
        if (typeof e === 'string') event = e;
      }
    }
  } catch {
    return NO_SIGNAL;
  }

  const isOperatorMessage = authorType === 'OPERATOR';
  const isConsumerMessage = authorType === 'CONSUMER';
  const isNewApproval = kind === 'APPROVAL_REQUEST' && status === 'PENDING';
  const isNewNoShowCard = kind === 'NO_SHOW_DECISION' && status === 'PENDING';
  const isNoShowAutoCancel = event === 'NO_SHOW_AUTO_CANCELLED';
  if (
    !isOperatorMessage &&
    !isConsumerMessage &&
    !isNewApproval &&
    !isNewNoShowCard &&
    !isNoShowAutoCancel
  ) {
    return NO_SIGNAL;
  }
  return { signal: true, isApproval: isNewApproval || isNewNoShowCard };
}
